import { Box, Grid2 } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import BackIcon from '@assets/images/BackIcon.svg';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import {
  NEW_PARTNER_ROUTE_PATH,
  PARTNER_ROUTE_PATH,
} from '../../routers/routePaths';
import {
  useGetPartnerDetailsById,
  useGetPartnerMetaData,
} from '../../API/hooks/PartnerHook';
import moment from 'moment';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import { hasRole } from '../login/storageUtility';
const PartnerView = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { partnerId } = useParams<{ partnerId?: string }>();

  const { PartnerDetailsById, isLoading } = useGetPartnerDetailsById(
    userId,
    Number(partnerId)
  );
  const { PartnerMetaData } = useGetPartnerMetaData(userId);
  const displayValueOrEmpty = (value: string | null | undefined): string => {
    return value === '0' ? 'N/A' : String(value);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  const handleEditPartnerInfo = (partnerId: number) => {
    navigate(NEW_PARTNER_ROUTE_PATH + `/${partnerId}`, { replace: true });
  };
  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="flex-justify-space-between align-items-baseline">
              <Box className="text-align-left display-flex">
                <CustomIconButton
                  id="back-button"
                  icon={<BackIcon />}
                  onClick={() => {
                    navigate(PARTNER_ROUTE_PATH);
                  }}
                ></CustomIconButton>
                <CustomTypography
                  className="pl-8"
                  variant="h4"
                  id="view-partner-text"
                >
                  View Partner - ({PartnerDetailsById?.partnerInfo.name})
                </CustomTypography>
              </Box>
              {hasRole(['ops-admin']) && (
                <Box>
                  <CustomButton
                    id="save-button"
                    onClick={() => {
                      handleEditPartnerInfo(
                        PartnerDetailsById?.partnerInfo.id || 0
                      );
                    }}
                    variant="contained"
                  >
                    Edit
                  </CustomButton>
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="form-background p-24">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <CustomTypography
              variant="h5"
              className="text-align-left pb-32"
              id="facility-info-text"
            >
              Partner Information
            </CustomTypography>
          </Grid2>
        </Grid2>
        <Box className="display-flex">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="partner-name-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Partner Name
              </CustomTypography>
              <CustomTypography
                id="partner-name-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo?.name || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="alias-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Alias
              </CustomTypography>
              <CustomTypography
                id="alias-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo.alias || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="status-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Status
              </CustomTypography>
              <CustomTypography
                id="status-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo.status ? (
                  <Box className="display-flex align-items-baseline">
                    <Box component="span" className="status-active-dot " />
                    <CustomTypography
                      id="partner-view-status-active-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pl-4"
                    >
                      {' '}
                      Active
                    </CustomTypography>
                  </Box>
                ) : (
                  <Box className="display-flex align-items-baseline">
                    <Box component="span" className="status-inactive-dot " />
                    <CustomTypography
                      id="partner-view-status-inactive-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pl-4"
                    >
                      {' '}
                      Inactive
                    </CustomTypography>
                  </Box>
                )}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="type-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Type
              </CustomTypography>
              <CustomTypography
                id="type-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerMetaData?.partnerTypes.find(
                  (item) =>
                    item.id === PartnerDetailsById?.partnerInfo.partnerTypeId
                )?.name || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="website-label"
                variant="body3"
                color="textPrimary"
                className="text-align-left pb-12"
              >
                Website
              </CustomTypography>
              <CustomTypography
                id="website-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo.website || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="services-provided-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Services Provided
              </CustomTypography>
              <CustomTypography
                id="services-provided-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo?.servicesProvided?.length
                  ? PartnerDetailsById.partnerInfo.servicesProvided
                      .map((item) => item.name)
                      .join(', ')
                  : 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="active-since-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Active Partner Since
              </CustomTypography>
              <CustomTypography
                id="active-since-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo?.activePartnerSince
                  ? moment(
                      PartnerDetailsById.partnerInfo.activePartnerSince
                    ).format('MM-DD-YYYY')
                  : 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="termination-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Termination Date
              </CustomTypography>
              <CustomTypography
                id="termination-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.partnerInfo?.terminationDate
                  ? moment(
                      PartnerDetailsById.partnerInfo.terminationDate
                    ).format('MM-DD-YYYY')
                  : 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="assigned-to-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Assigned To
              </CustomTypography>
              <CustomTypography
                id="assigned-to-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerMetaData?.assignedTo.find(
                  (item) =>
                    item.id === PartnerDetailsById?.partnerInfo.assignedTo
                )?.name || 'N/A'}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>
        <Box className="pt-32">
          <CustomDivider />
        </Box>
        <Box className="pt-32">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <CustomTypography
                variant="h5"
                className="text-align-left pb-32"
                id="facility-info-text"
              >
                Key Phone Numbers and Email
              </CustomTypography>
            </Grid2>
          </Grid2>
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="primary-phone-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Primary Phone
              </CustomTypography>
              <CustomTypography
                id="primary-phone-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.primaryPhone || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="IT-help-desk-phone-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                IT Help Desk Phone
              </CustomTypography>
              <CustomTypography
                id="IT-help-desk-phone-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.itHelpDeskPhone || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="IT-help-desk-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                IT Help Desk Email
              </CustomTypography>
              <CustomTypography
                id="IT-help-desk-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.itHelpDeskEmail || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="contact-email-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Contact Email
              </CustomTypography>
              <CustomTypography
                id="contact-email-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.contactEmail || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="fax-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Fax
              </CustomTypography>
              <CustomTypography
                id="fax-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.fax || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="other-email-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Other Email
              </CustomTypography>
              <CustomTypography
                id="other-email-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.otherEmail || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="fax-description-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Fax Description
              </CustomTypography>
              <CustomTypography
                id="fax-description-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.faxDescription || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="other-email-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Other Email Description
              </CustomTypography>
              <CustomTypography
                id="other-email-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.contactDetails.otherEmailDescription ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>
        <Box className="pt-32">
          <CustomDivider />
        </Box>
        <Box className="pt-32">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <CustomTypography
                variant="h5"
                className="text-align-left pb-32"
                id="it-system-text"
              >
                IT System Details
              </CustomTypography>
            </Grid2>
          </Grid2>
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="network-access-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Network Access Type
              </CustomTypography>
              <CustomTypography
                id="network-access-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.networkAccessType || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="emr-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                EMR
              </CustomTypography>
              <CustomTypography
                id="emr-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.emr || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="email-domain-primary-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Email Domain Primary
              </CustomTypography>
              <CustomTypography
                id="email-domain-primary-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.emailDomainPrimary ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="network-access-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Network Access URL
              </CustomTypography>
              <CustomTypography
                id="network-access-url-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.networkAccessUrl || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="documentation-in-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Documentation In
              </CustomTypography>
              <CustomTypography
                id="documentation-in-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.documentationIn || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="email-domain-secondary-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Email Domain Secondary
              </CustomTypography>
              <CustomTypography
                id="email-domain-secondary-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.emailDomainSecondary ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="vpn-gateway-software-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                VPN Gateway Software
              </CustomTypography>
              <CustomTypography
                id="vpn-gateway-software-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.vpnGatewaySoftware ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="telemedicine-platform-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Telemedicine Platform
              </CustomTypography>
              <CustomTypography
                id="telemedicine-platform-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.telemedicinePlatform ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="email-domain-tertiary-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Email Domain Tertiary
              </CustomTypography>
              <CustomTypography
                id="email-domain-tertiary-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.emailDomainTertiary ||
                  'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="mfa-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                MFA
              </CustomTypography>
              <CustomTypography
                id="mfa-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.mfa || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="telemedicine-platform-url-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Telemedicine Platform URL
              </CustomTypography>
              <CustomTypography
                id="telemedicine-platform-url-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.telemedicinePlatformUrl ||
                  'N/A'}
              </CustomTypography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="training-equipment-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Training Requirement
              </CustomTypography>
              <CustomTypography
                id="training-equipment-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.trainingEquipment || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="devices-utilized-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Devices Utilized
              </CustomTypography>
              <CustomTypography
                id="devices-utilized-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.devicesUtilized || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="it-notes-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                IT Notes
              </CustomTypography>
              <CustomTypography
                id="it-notes-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.itSystemDetails.itNotes || 'N/A'}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>

        <Box className="pt-32">
          <CustomDivider />
        </Box>
        <Box className="pt-32">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <CustomTypography
                variant="h5"
                className="text-align-left pb-32"
                id="address-details-text"
              >
                Address Details
              </CustomTypography>
            </Grid2>
          </Grid2>
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="physical-address-line-1-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Address Line 1
              </CustomTypography>
              <CustomTypography
                id="physical-address-line-1-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.address.addressLine1 || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="physical-city-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                City
              </CustomTypography>
              <CustomTypography
                id="physical-city-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.address.city || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="physical-postal-code-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Postal Code
              </CustomTypography>
              <CustomTypography
                id="physical-postal-code-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.address.postalCode || 'N/A'}
              </CustomTypography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="physical-address-line-2-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Address Line 2
              </CustomTypography>
              <CustomTypography
                id="physical-address-line-2-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.address.addressLine2 || 'N/A'}
              </CustomTypography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="physical-state-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                State
              </CustomTypography>
              <CustomTypography
                id="physical-state-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {displayValueOrEmpty(PartnerDetailsById?.address.state)}
              </CustomTypography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <CustomTypography
                id="physical-country-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                County
              </CustomTypography>
              <CustomTypography
                id="physical-country-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.address.county || 'N/A'}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>
        <Box className="pt-32">
          <CustomDivider />
        </Box>
        <Box className="pt-32">
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <CustomTypography
                variant="h5"
                className="text-align-left pb-32"
                id="description-details-text"
              >
                Additional Details
              </CustomTypography>
            </Grid2>
          </Grid2>
          <Grid2 container spacing={4} className="width-80">
            <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
              <CustomTypography
                id="description-label"
                variant="body3"
                color="textDisabled"
                className="text-align-left pb-12"
              >
                Description
              </CustomTypography>
              <CustomTypography
                id="description-value"
                variant="body3"
                color="textPrimary"
                className="text-align-left"
              >
                {PartnerDetailsById?.description || 'N/A'}
              </CustomTypography>
            </Grid2>
          </Grid2>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerView;
